import { type ReactNode, Suspense, lazy } from "react";
import { Navigate, createBrowserRouter } from "react-router";

import { DefaultMarketRedirect } from "@components/DefaultMarketRedirect";

import { Home } from "@screens/Home";
import { Maintenance } from "@screens/Maintenance";
import { NotFound } from "@screens/NotFound";

import { maintenanceConfig } from "./config/maintenance";
import { GlobalLayout } from "./layouts/GlobalLayout";

const ApiKeysScreen = lazy(() => import("@screens/ApiKeys").then((m) => ({ default: m.ApiKeys })));
const PortfolioScreen = lazy(() => import("@screens/Portfolio").then((m) => ({ default: m.Portfolio })));
const LeaderboardScreen = lazy(() => import("@screens/Leaderboard").then((m) => ({ default: m.Leaderboard })));
const CompetitionsScreen = lazy(() => import("@screens/Competitions").then((m) => ({ default: m.Competitions })));
const StatsScreen = lazy(() => import("@screens/Stats").then((m) => ({ default: m.Stats })));

const withSuspense = (element: ReactNode) => <Suspense fallback={null}>{element}</Suspense>;

const createRouterConfig = () => {
  if (maintenanceConfig.isMaintenanceMode) {
    return [
      {
        path: "*",
        element: <Maintenance />,
      },
    ];
  }

  return [
    {
      path: "/",
      element: <GlobalLayout />,
      children: [
        { index: true, element: <DefaultMarketRedirect /> },
        { path: "trades", element: <DefaultMarketRedirect /> },
        { path: "trades/:marketId", element: <Home /> },
        { path: "portfolio", element: withSuspense(<PortfolioScreen />) },
        { path: "portfolio-with-xp", element: withSuspense(<PortfolioScreen withXp />) },
        { path: "api-keys", element: withSuspense(<ApiKeysScreen />) },
        { path: "referral", element: <NotFound /> },
        { path: "leaderboard", element: withSuspense(<LeaderboardScreen />) },
        { path: "leaderboard-with-xp", element: withSuspense(<LeaderboardScreen withXp />) },
        { path: "competitions", element: <Navigate to="/competitions/live" replace /> },
        { path: "competitions/future", element: withSuspense(<CompetitionsScreen />) },
        { path: "competitions/live", element: withSuspense(<CompetitionsScreen />) },
        { path: "competitions/past", element: withSuspense(<CompetitionsScreen />) },
        { path: "stats", element: withSuspense(<StatsScreen />) },
        { path: "*", element: <NotFound /> },
      ],
    },
    {
      path: "/debug",
      element: <GlobalLayout />,
      children: [{ path: "stats", element: withSuspense(<StatsScreen />) }],
    },
  ];
};

export const router = createBrowserRouter(createRouterConfig());
